import { CaretSortIcon, Cross2Icon } from '@radix-ui/react-icons';
import { animated, useTransition } from '@react-spring/web';
import React from 'react';
import { t } from 'i18next';
import { useShallow } from 'zustand/react/shallow';
import { useWindowSize } from '../../../app/hooks/useWindowSize';
import { useStore } from '../../../app/store';
import cn from '../../../app/utils/cn';
import { IconButton } from '../IconButton';
import Logo from '../Logo';
import { useAuth } from '../../../app/hooks/useAuth';

const AnimatedParagraph = animated('p');

export const SidebarHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  const { toggleSidebar, isOpen } = useStore(
    useShallow((state) => ({
      toggleSidebar: state.sidebar.toggleSidebar,
      isOpen: state.sidebar.isOpen,
    })),
  );
  const { isMobile } = useWindowSize();
  const { loggedInUser: user, tenant } = useAuth();

  const showOrganizationsIcon =
    user && user.memberships.length > 1 && !isMobile && isOpen;

  const textTransition = useTransition(isOpen, {
    from: { opacity: 0, transform: 'translateX(-40px)' },
    leave: { opacity: 0, transform: 'translateX(0)' },
    enter: {
      opacity: 1,
      transform: 'translateX(0px)',
      delay: 50,
    },
    config: { duration: !isOpen ? 0 : 200 },
    exitBeforeEnter: true,
  });

  return (
    <header
      ref={ref}
      className={cn(
        'hover:bg-grey-0/50 dark:hover:bg-grey-700/50 flex h-12 cursor-pointer items-center justify-between gap-2 rounded-xl select-none',
        !isOpen && 'justify-center',
        className,
      )}
      {...props}
    >
      <div className="flex grow items-center gap-2 px-2">
        <span className="flex h-full items-center">
          <Logo variant="micro" className="h-6 w-6" />
        </span>

        {textTransition((animation, item) =>
          item ? (
            <AnimatedParagraph
              style={animation}
              className="text-text-brand text-sm font-bold whitespace-nowrap"
            >
              {tenant?.name}
            </AnimatedParagraph>
          ) : null,
        )}
      </div>

      {showOrganizationsIcon && (
        <CaretSortIcon className="text-text-primary size-6" />
      )}

      {isMobile && (
        <IconButton
          size="md"
          aria-label={t('sidenavbar.actions.closeSidebar', {
            defaultValue: 'Fechar menu',
          })}
          onClick={() => toggleSidebar()}
          className={cn(
            'text-green-800 dark:text-green-300',
            isOpen
              ? 'md:hover:animate-[bounce-left_1s_ease-in-out_infinite]'
              : 'md:hover:animate-[bounce-left_1s_ease-in-out_infinite_reverse]',
          )}
        >
          <Cross2Icon className="size-5" />
        </IconButton>
      )}
    </header>
  );
});

SidebarHeader.displayName = 'SidebarHeader';
